import { useState } from "react";
import { Link, useParams } from "react-router";
import { useCanWrite } from "../auth/AuthProvider";
import { DeleteWorkloadsModal, type ManifestItem } from "../components/DeleteWorkloadsModal";
import { SelectionBar } from "../components/SelectionBar";
import { OrgCreditCard } from "../components/OrgCreditCard";
import { OrgLimitsCard } from "../components/OrgLimitsCard";
import { OrgRiskCard } from "../components/OrgRiskCard";
import { SuspendOrgModal } from "../components/SuspendOrgModal";
import { ServerPicker } from "../components/ServerPicker";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  LoadingBlock,
  Mono,
  PageHeader,
  RoleBadge,
  Table,
  Tag,
  Td,
  Th,
  buttonStyles,
} from "../components/ui";
import { formatDate, formatDateTime, formatRelative } from "../lib/format";
import {
  useCancelOrgClosure,
  useDeleteOrgWorkloads,
  useDomainOrders,
  useFleetDomains,
  useIsolatePlacement,
  useOrg,
  useRegionLookup,
  useUpdateOrg,
} from "../lib/queries";
import { groupLabel } from "../lib/regions";
import type { AdminOrgDetail, Placement, RegisteredDomainStatus, WorkloadResult } from "../lib/types";

/**
 * The deletion moment, with its zone.
 *
 * `formatDateTime` drops the zone, which is fine for a created-at and not for
 * this: an operator reading "7:00 PM" while deciding whether there is time to
 * warn a customer needs to know whose 7:00 PM it is.
 */
function closingMoment(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

/**
 * What a placement's network column says, and which step is next.
 *
 * "Shared" is every workload on the server's one overlay -- the state every
 * placement was in before tenant networks. The two steps are the API's two,
 * in its order: applications join (rolling, nothing stops), and only once
 * every one of them is on the network do the databases and tunnels leave the
 * shared overlay, each with one restart. The second button is enabled by
 * the first's counts rather than by a flag, so a placement half-way through
 * shows exactly what is left.
 */
function isolationState(placement: Placement): {
  label: string;
  tone: "good" | "warn" | "neutral";
  next: "applications" | "sealed" | null;
} {
  const { network, apps, databases, tunnels } = placement.isolation;
  const sealedTotal = databases.total + tunnels.total;
  const sealedAttached = databases.attached + tunnels.attached;
  if (!network) {
    return {
      label: "Shared",
      tone: apps.total + sealedTotal > 0 ? "warn" : "neutral",
      next: apps.total + sealedTotal > 0 ? "applications" : null,
    };
  }
  if (apps.attached < apps.total) {
    return { label: `Apps ${apps.attached}/${apps.total} attached`, tone: "warn", next: "applications" };
  }
  if (sealedAttached < sealedTotal) {
    return { label: `Databases ${sealedAttached}/${sealedTotal} sealed`, tone: "warn", next: "sealed" };
  }
  return { label: "Isolated", tone: "good", next: null };
}

export function OrgDetailPage() {
  const { orgId = "" } = useParams();
  const { data, isPending, error } = useOrg(orgId);
  const canWrite = useCanWrite();
  const isolate = useIsolatePlacement(orgId);
  const update = useUpdateOrg(orgId);
  const cancelClosure = useCancelOrgClosure(orgId);
  const [suspending, setSuspending] = useState(false);

  /*
   * What the operator has ticked, and what the delete dialog is for. One
   * dialog serves both the ticked rows and "everything": the selection is
   * the request, and `all` is a request with no ids.
   */
  const [selectedApps, setSelectedApps] = useState<Set<string>>(() => new Set());
  const [selectedDatabases, setSelectedDatabases] = useState<Set<string>>(() => new Set());
  const [deleting, setDeleting] = useState<"selected" | "all" | null>(null);
  const [deleteResults, setDeleteResults] = useState<WorkloadResult[] | null>(null);
  const deleteWorkloads = useDeleteOrgWorkloads(orgId);

  if (isPending) return <LoadingBlock />;
  if (error) return <Alert>{error.message}</Alert>;

  const workloadCount = data.apps.length + data.databases.length;
  const selectedCount = selectedApps.size + selectedDatabases.size;
  const toggle = (set: Set<string>, id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  };
  const allApps = data.apps.length > 0 && selectedApps.size === data.apps.length;
  const allDatabases = data.databases.length > 0 && selectedDatabases.size === data.databases.length;
  const clearSelection = () => {
    setSelectedApps(new Set());
    setSelectedDatabases(new Set());
  };

  /*
   * The manifest the dialog shows: what is about to go, one row each, with the
   * one fact that tells rows apart — an app's address, a database's engine and
   * whether it is published. "All" lists everything; "selected" lists the
   * ticked rows, in table order.
   */
  const manifest: ManifestItem[] = [
    ...data.apps
      .filter((app) => deleting === "all" || selectedApps.has(app.id))
      .map((app) => ({
        kind: "app" as const,
        id: app.id,
        name: app.name,
        detail: app.defaultDomain ?? `${app.source.type} · no address yet`,
      })),
    ...data.databases
      .filter((database) => deleting === "all" || selectedDatabases.has(database.id))
      .map((database) => ({
        kind: "database" as const,
        id: database.id,
        name: database.name,
        detail: database.engine,
        flag: database.externalPort !== null ? `published :${database.externalPort}` : undefined,
      })),
  ];

  return (
    <>
      <PageHeader
        title={data.name}
        description={data.slug}
        action={
          <div className="flex flex-wrap gap-2">
            <Link to={`/audit?orgId=${data.id}`} className={buttonStyles("secondary", "md")}>
              Audit activity
            </Link>
            {canWrite && (
              <>
                {data.suspendedAt ? (
                  <Button
                    loading={update.isPending}
                    onClick={() => update.mutate({ suspended: false })}
                  >
                    Unsuspend
                  </Button>
                ) : (
                  <Button variant="danger" onClick={() => setSuspending(true)}>
                    Suspend
                  </Button>
                )}
                {/* The step after a suspension, for a tenant who is not coming
                    back. Offered whenever there is something to delete: the
                    dialog asks for the name either way. */}
                {workloadCount > 0 && (
                  <Button variant="danger" onClick={() => setDeleting("all")}>
                    Delete all workloads
                  </Button>
                )}
              </>
            )}
          </div>
        }
      />

      {update.error && <Alert>{update.error.message}</Alert>}
      {cancelClosure.error && <Alert>{cancelClosure.error.message}</Alert>}

      {/*
        The credit timeline, which has a deadline of its own. The date is the
        thing a customer writes in about; the fact is why the workload tables
        below may be empty on an organization that clearly used to have some.
      */}
      {data.creditTerminatedAt ? (
        <Alert tone="warning">
          <p className="font-medium">
            Workloads deleted for credit {formatRelative(data.creditTerminatedAt)}.
          </p>
          <p className="mt-0.5">
            Stopped {data.creditSuspendedAt ? formatRelative(data.creditSuspendedAt) : "earlier"}, told
            the date on the fourteenth day, torn down on the twenty-first. The organization, its
            members and its billing history remain; a top-up lets them start again with nothing.
          </p>
        </Alert>
      ) : data.creditTerminatesAt ? (
        <Alert tone="error">
          <p className="font-medium">
            Workloads deleted for credit {formatRelative(data.creditTerminatesAt)} —{" "}
            {closingMoment(data.creditTerminatesAt)}.
          </p>
          <p className="mt-0.5">
            Stopped for credit {data.creditSuspendedAt ? formatRelative(data.creditSuspendedAt) : "earlier"}{" "}
            with nothing added since. The customer was mailed the date; a top-up of any amount
            cancels it. A credit adjustment from the billing card does the same, and so does making
            a platform operator an owner — staff-owned organizations are never terminated.
          </p>
        </Alert>
      ) : null}

      {/*
        Above the suspension banner because it is the state with a deadline, and
        because it is the one support gets asked about: an organization that is
        read-only for a reason nobody in the console could previously see.
      */}
      {data.closesAt && (
        <Alert tone="error">
          <p className="font-medium">
            Closing {formatRelative(data.closesAt)} — {closingMoment(data.closesAt)}.
          </p>
          <p className="mt-0.5">
            The customer scheduled this
            {data.closureRequestedAt ? ` ${formatRelative(data.closureRequestedAt)}` : ""}. The
            organization is read-only until then, and the sweep deletes everything in it when the
            time comes. Any owner can call it off themselves; do it here for somebody who cannot.
          </p>
          {canWrite && (
            <button
              type="button"
              disabled={cancelClosure.isPending}
              onClick={() => cancelClosure.mutate()}
              className="mt-1.5 text-xs font-medium underline underline-offset-2 disabled:opacity-50"
            >
              Cancel the closure
            </button>
          )}
        </Alert>
      )}

      {data.suspendedAt && (
        <Alert tone="warning">
          <p className="font-medium">Suspended {formatRelative(data.suspendedAt)}.</p>
          {/* The operator's own words on their own line. Run together with the
              sentence below, an unpunctuated reason reads as one broken
              sentence — "Chargeback under review Members keep read access". */}
          <p className="mt-0.5 text-fg">{data.suspendedReason ?? "No reason recorded."}</p>
          <p className="mt-0.5">
            Members keep read access but cannot deploy or change anything.
          </p>
          {/*
            Both facts an operator cannot get anywhere else. The workloads line
            because a suspension reads like a stop and is not one — they are
            still running and still being charged. The payment line because
            "refuse top-ups too" was otherwise a write-only setting: it could be
            turned on at suspend time and never seen again.
          */}
          <p className="mt-0.5">
            Applications and databases are still running and still being charged.{" "}
            {data.suspensionBlocksPayment
              ? "Top-ups are refused as well."
              : "They can still add credit, which will not lift this."}
            {workloadCount > 0 && " Delete all workloads, above, is how that stops."}
          </p>
          {/*
            Toggled here rather than only at suspend time. The alternative for an
            operator who learns of a chargeback after suspending was to unsuspend
            and suspend again — which mails the customer "your organization is
            active again" in between, over a state that lasted one request.
          */}
          {canWrite && (
            <button
              type="button"
              disabled={update.isPending}
              onClick={() => update.mutate({ blockPayment: !data.suspensionBlocksPayment })}
              className="mt-1.5 text-xs font-medium underline underline-offset-2 disabled:opacity-50"
            >
              {data.suspensionBlocksPayment ? "Accept top-ups again" : "Refuse top-ups too"}
            </button>
          )}
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Tenant" />
          <dl className="grid gap-x-6 gap-y-4 px-5 py-4 sm:grid-cols-2">
            {/*
              Where the tenant is, as one line; the Placements card below has
              the why. An organization can be in several regions now, each on
              its own server with its own project, so the fact here is the set.
            */}
            <div className="sm:col-span-2">
              <dt className="text-xs uppercase tracking-wide text-fg-subtle">Regions</dt>
              <dd className="mt-1 text-sm">
                {data.placements.length === 0 ? (
                  <span className="text-fg-subtle">Unplaced</span>
                ) : (
                  <span className="flex flex-wrap gap-1.5">
                    {data.placements.map((placement) => (
                      <Tag key={placement.id}>{groupLabel(placement.region) ?? "No region"}</Tag>
                    ))}
                  </span>
                )}
                <p className="mt-1.5 text-xs text-fg-subtle">
                  {data.pinnedServerId
                    ? "Pinned to one server by an operator. Every region this tenant deploys in is that server."
                    : `Placed per region on first use; Auto picks the nearest to ${describeOrigin(data.placedFrom)}.`}
                </p>
              </dd>
            </div>
            {/* Closing outranks suspended, and both outrank active: an
                organization being deleted in a week is not "Active". */}
            <Detail
              label="Status"
              value={data.closesAt ? "Closing" : data.suspendedAt ? "Suspended" : "Active"}
            />
            <Detail label="Members" value={String(data.counts?.members ?? data.members.length)} />
            <Detail label="Apps" value={String(data.counts?.apps ?? data.apps.length)} />
            <Detail label="Created" value={formatDateTime(data.createdAt)} />
          </dl>
        </Card>

        <Card>
          <CardHeader title="Members" description={`${data.members.length} total`} />
          {data.members.length === 0 ? (
            <EmptyState title="No members" />
          ) : (
            <ul className="divide-y divide-line">
              {data.members.map((member) => (
                <li key={member.id} className="flex items-center justify-between gap-3 px-5 py-3">
                  <div className="min-w-0">
                    <Link
                      to={`/users/${member.id}`}
                      className="text-sm font-medium text-accent hover:underline"
                    >
                      {member.name}
                    </Link>
                    <p className="truncate text-xs text-fg-subtle">
                      {member.email}
                    </p>
                  </div>
                  <RoleBadge role={member.role} />
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/*
        One row per placement: the region, the box, what lives there, and why
        it is there. "Why is this customer in Frankfurt" is a real support
        question, and with several regions per tenant the answer is per row —
        a customer's own choice in one region and Auto's in another.
      */}
      <Card>
        <CardHeader
          title="Placements"
          description={
            data.placements.length === 0
              ? "One is made per region when the first app or database is created there."
              : `${data.placements.length} region${data.placements.length === 1 ? "" : "s"}, each on its own server with its own project`
          }
        />
        {data.placements.length === 0 ? (
          <EmptyState title="Nothing placed yet" />
        ) : (
          <Table
            head={
              <tr>
                <Th>Region</Th>
                <Th>Server</Th>
                <Th numeric>Apps</Th>
                <Th numeric>Databases</Th>
                <Th>Network</Th>
                <Th priority="md">Chosen</Th>
                <Th priority="lg">Project</Th>
              </tr>
            }
          >
            {data.placements.map((placement) => {
              const isolation = isolationState(placement);
              return (
              <tr key={placement.id} className="hover:bg-raised">
                <Td className="font-medium">{groupLabel(placement.region) ?? "No region"}</Td>
                <Td>
                  {placement.server ? (
                    <Link
                      to={`/servers/${placement.server.id}`}
                      className="text-accent hover:underline"
                    >
                      {placement.server.name ?? placement.server.id}
                    </Link>
                  ) : (
                    // The server was deregistered while this placement was
                    // empty; the next create in the region claims a fresh one.
                    <span className="text-warn">Deregistered</span>
                  )}
                </Td>
                <Td numeric>{placement.counts.apps}</Td>
                <Td numeric>{placement.counts.databases}</Td>
                <Td>
                  <div className="flex flex-wrap items-center gap-2">
                    <Tag tone={isolation.tone}>{isolation.label}</Tag>
                    {placement.isolation.network && (
                      <Mono className="text-fg-subtle">{placement.isolation.network.name}</Mono>
                    )}
                    {canWrite && isolation.next && placement.server && (
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={isolate.isPending && isolate.variables?.placementId === placement.id}
                        onClick={() => isolate.mutate({ placementId: placement.id, step: isolation.next! })}
                      >
                        {isolation.next === "applications" ? "Attach apps" : "Seal databases"}
                      </Button>
                    )}
                  </div>
                  {isolate.isError && isolate.variables?.placementId === placement.id && (
                    <p className="mt-1 text-xs text-danger">{isolate.error.message}</p>
                  )}
                </Td>
                <Td className="text-fg-subtle" priority="md">
                  {placement.requestedRegion
                    ? `By the customer (${groupLabel(placement.requestedRegion)})`
                    : "Automatically"}
                </Td>
                <Td priority="lg">
                  {placement.dokploy.projectId ? (
                    <Mono>{placement.dokploy.projectId}</Mono>
                  ) : (
                    <span className="text-fg-subtle">Not provisioned yet</span>
                  )}
                </Td>
              </tr>
              );
            })}
          </Table>
        )}
      </Card>

      <OrgCreditCard orgId={data.id} canWrite={canWrite} />

      <OrgLimitsCard orgId={data.id} canWrite={canWrite} />
      <OrgRiskCard orgId={data.id} />

      <Card>
        <CardHeader title="Apps" description={`${data.apps.length} in this organization`} />
        {data.apps.length === 0 ? (
          <EmptyState title="No apps yet" />
        ) : (
          // No state column, for the reason spelled out in AppsPage: the stored
          // value is the last one we asked for, not the one the engine reports.
          <Table
            head={
              <tr>
                {canWrite && (
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      aria-label="Select every app"
                      checked={allApps}
                      onChange={() =>
                        setSelectedApps(allApps ? new Set() : new Set(data.apps.map((app) => app.id)))
                      }
                      className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </Th>
                )}
                <Th>App</Th>
                <Th priority="sm">Source</Th>
                <Th>Updated</Th>
              </tr>
            }
          >
            {data.apps.map((app) => (
              <tr key={app.id} className="hover:bg-raised">
                {canWrite && (
                  <Td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${app.name}`}
                      checked={selectedApps.has(app.id)}
                      onChange={() => setSelectedApps((set) => toggle(set, app.id))}
                      className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </Td>
                )}
                <Td>
                  <Link
                    to={`/apps/${app.id}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {app.name}
                  </Link>
                    {app.replicas > 1 && (
                      <span className="ml-2" title={app.autoscale ? `Auto-scale ${app.autoscale.min}–${app.autoscale.max} at ${app.autoscale.targetCpu}% CPU` : "Set by the customer"}>
                        <Tag>×{app.replicas}{app.autoscale ? " auto" : ""}</Tag>
                      </span>
                    )}
                </Td>
                <Td className="capitalize text-fg-subtle" priority="sm">{app.source.type}</Td>
                <Td className="text-fg-subtle">{formatRelative(app.updatedAt)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/*
        Beside the apps for the first time. A tenant is not cleared while its
        database is still there and still charged, and until now the console
        could not see one from here at all.
      */}
      <Card>
        <CardHeader title="Databases" description={`${data.databases.length} in this organization`} />
        {data.databases.length === 0 ? (
          <EmptyState title="No databases yet" />
        ) : (
          <Table
            head={
              <tr>
                {canWrite && (
                  <Th className="w-8">
                    <input
                      type="checkbox"
                      aria-label="Select every database"
                      checked={allDatabases}
                      onChange={() =>
                        setSelectedDatabases(
                          allDatabases ? new Set() : new Set(data.databases.map((database) => database.id)),
                        )
                      }
                      className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </Th>
                )}
                <Th>Database</Th>
                <Th priority="sm">Engine</Th>
                <Th priority="md">Published</Th>
                <Th>Updated</Th>
              </tr>
            }
          >
            {data.databases.map((database) => (
              <tr key={database.id} className="hover:bg-raised">
                {canWrite && (
                  <Td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${database.name}`}
                      checked={selectedDatabases.has(database.id)}
                      onChange={() => setSelectedDatabases((set) => toggle(set, database.id))}
                      className="h-4 w-4 rounded border-line text-accent focus:ring-2 focus:ring-accent/20"
                    />
                  </Td>
                )}
                <Td>
                  <Link
                    to={`/databases?q=${encodeURIComponent(database.name)}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {database.name}
                  </Link>
                </Td>
                <Td className="text-fg-subtle" priority="sm">{database.engine}</Td>
                <Td className="text-fg-subtle" priority="md">
                  {database.externalPort !== null ? `:${database.externalPort}` : "Private"}
                </Td>
                <Td className="text-fg-subtle">{formatRelative(database.updatedAt)}</Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <OrgDomains orgId={data.id} />

      {canWrite && (
        <SelectionBar
          count={selectedCount}
          noun="workload"
          onClear={clearSelection}
          action={
            <Button variant="danger" size="sm" icon="trash" onClick={() => setDeleting("selected")}>
              Delete selected
            </Button>
          }
        >
          {selectedApps.size > 0 && `${selectedApps.size} app${selectedApps.size === 1 ? "" : "s"}`}
          {selectedApps.size > 0 && selectedDatabases.size > 0 && " · "}
          {selectedDatabases.size > 0 &&
            `${selectedDatabases.size} database${selectedDatabases.size === 1 ? "" : "s"}`}
        </SelectionBar>
      )}

      <DeleteWorkloadsModal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={deleting === "all" ? `Delete everything in ${data.name}` : `Delete from ${data.name}`}
        description="The organization, its members and its billing history stay."
        items={manifest}
        noun="workload"
        confirmName={data.name}
        confirmLabel={deleting === "all" ? "Delete all workloads" : `Delete ${manifest.length} workload${manifest.length === 1 ? "" : "s"}`}
        run={async ({ force, otp }) => {
          const response = await deleteWorkloads.mutateAsync(
            deleting === "all"
              ? { all: true, force, otp }
              : { appIds: [...selectedApps], databaseIds: [...selectedDatabases], force, otp },
          );
          setDeleteResults(response.results);
        }}
        pending={deleteWorkloads.isPending}
        results={deleteResults}
        onDone={() => {
          setDeleting(null);
          setDeleteResults(null);
          clearSelection();
        }}
      />

      {canWrite && (
        <DedicatedServerCard
          orgId={orgId}
          pinnedServerId={data.pinnedServerId}
          currentServerName={
            data.placements.find((p) => p.server?.id === data.pinnedServerId)?.server?.name ??
            null
          }
          hasWorkloads={(data.counts?.apps ?? data.apps.length) + data.databases.length > 0}
        />
      )}

      <SuspendOrgModal
        orgId={orgId}
        open={suspending}
        onClose={() => setSuspending(false)}
        name={data.name}
      />
    </>
  );
}

/**
 * The origin placement used, at whatever precision it had.
 *
 * Coordinates are shown rounded to whole degrees. They are a city-level guess
 * to begin with, and printing four decimals would claim a precision about a
 * customer's location that we neither have nor want.
 */
function describeOrigin(origin: AdminOrgDetail["placedFrom"]): string {
  const where = origin.country ?? origin.continent;
  // Phrased to sit inside a sentence — it is always read as "chosen from …".
  if (!where && !origin.coordinates) return "an unrecorded location";
  if (!origin.coordinates) return where ?? "an unrecorded location";

  const point = `${Math.round(origin.coordinates.latitude)}, ${Math.round(origin.coordinates.longitude)}`;
  return where ? `${where} (${point})` : point;
}

/**
 * Pinning a tenant to one server, for a dedicated plan.
 *
 * Deliberately not the old "Change server" action back again. That one moved
 * organizations that already held work, and its guard counted applications but
 * not databases — so a tenant could be moved while its database stayed behind.
 *
 * Nothing is relocated here. A pin only decides where an organization's
 * placements land — every region it deploys in becomes that one server — which
 * is why the whole card becomes read-only the moment it holds anything. The
 * API enforces the same rule; this just says so before the operator spends a
 * click on it.
 */
function DedicatedServerCard({
  orgId,
  pinnedServerId,
  currentServerName,
  hasWorkloads,
}: {
  orgId: string;
  pinnedServerId: string | null;
  currentServerName: string | null;
  hasWorkloads: boolean;
}) {
  const update = useUpdateOrg(orgId);
  const regionLookup = useRegionLookup();
  const [serverId, setServerId] = useState(pinnedServerId ?? "");

  return (
    <Card>
      <CardHeader
        title="Dedicated server"
        description="Fixes this tenant to one machine, instead of letting placement choose."
      />
      <div className="space-y-4 px-5 py-4">
        {update.error && <Alert>{update.error.message}</Alert>}
        {update.isSuccess && <Alert tone="success">Saved.</Alert>}

        {hasWorkloads ? (
          <Alert tone="info">
            {pinnedServerId
              ? `Pinned to ${currentServerName ?? "its server"}. This cannot be changed now that the organization has apps or databases — they cannot follow it to another machine.`
              : "This organization already has apps or databases, so its placements are fixed. They cannot be moved to another machine."}
          </Alert>
        ) : (
          <>
            <Field
              label="Server"
              htmlFor="pin-server"
              hint="Blank returns it to automatic placement."
            >
              <ServerPicker
                id="pin-server"
                value={serverId}
                onChange={setServerId}
                emptyLabel="Automatic (nearest with room)"
                initialLabel={pinnedServerId ? currentServerName : null}
                ariaLabel="Dedicated server"
                // A disabled server takes nobody, so offering it here would only
                // produce a refusal from the API a moment later.
                isDisabled={(server) => server.state === "disabled"}
                describe={(server) =>
                  [
                    server.region
                      ? (regionLookup.get(server.region)?.label ?? server.region)
                      : null,
                    server.pool === "dedicated" ? "dedicated" : null,
                    server.state !== "active" ? server.state : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || null
                }
              />
            </Field>

            <Button
              loading={update.isPending}
              disabled={(serverId || null) === pinnedServerId}
              onClick={() => update.mutate({ pinnedServerId: serverId || null })}
            >
              {serverId ? "Pin to this server" : "Return to automatic"}
            </Button>
          </>
        )}
      </div>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-fg-subtle">{label}</dt>
      <dd className="mt-1 text-sm">{value}</dd>
    </div>
  );
}

/**
 * The organization's registered names, beside its apps and databases. A
 * support conversation that starts "my domain" starts on this page, and
 * until this card the answer was a search on another one.
 */
export function OrgDomains({ orgId }: { orgId: string }) {
  const domains = useFleetDomains({ orgId, limit: 50 });
  const rows = domains.data?.data ?? [];
  const orders = useDomainOrders({ orgId, limit: 1 });
  const spent = orders.data?.total ?? 0;
  if (domains.isPending || rows.length === 0) return null;
  return (
    <Card>
      <CardHeader
        title="Domains"
        description={`${rows.length} registered here${spent ? ` · ${spent} order${spent === 1 ? "" : "s"}` : ""}`}
        action={
          <Link to={`/domains/orders?orgId=${orgId}`} className="text-[13px] text-accent hover:underline">
            Orders
          </Link>
        }
      />
      <Table
        head={
          <tr>
            <Th>Domain</Th>
            <Th priority="sm">Status</Th>
            <Th priority="md">Renews</Th>
            <Th priority="lg">DNS</Th>
          </tr>
        }
      >
        {rows.map((domain) => (
          <tr key={domain.id} className="hover:bg-raised">
            <Td>
              <Link to={`/domains?q=${encodeURIComponent(domain.domain)}`} className="font-mono text-[13px] text-accent hover:underline">
                {domain.domain}
              </Link>
              {domain.abuseFlags.length > 0 && (
                <span className="ml-2 text-[11px] text-warn">flagged: {domain.abuseFlags.join(", ")}</span>
              )}
              {domain.renewalError && <div className="mt-0.5 text-[11px] text-danger">{domain.renewalError}</div>}
            </Td>
            <Td priority="sm">
              <Tag tone={DOMAIN_TONE[domain.status]}>{domain.status}</Tag>
            </Td>
            <Td priority="md" className="text-fg-subtle">
              {domain.renewalDate ? formatDate(domain.renewalDate) : "—"}
              {domain.status === "active" && !domain.autorenew && " · autorenew off"}
            </Td>
            <Td priority="lg" className="text-fg-subtle">{domain.managedDns ? "hosted here" : "elsewhere"}</Td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

const DOMAIN_TONE: Record<RegisteredDomainStatus, "neutral" | "good" | "warn" | "bad"> = {
  pending: "warn",
  active: "good",
  failed: "bad",
  expired: "bad",
  deleted: "neutral",
};
